/**
 * A municipality dossier.
 *
 * Its procurements and its companies are each a full faceted list scoped to
 * this municipality, with their own filters, sort and page held in the same
 * URL under different prefixes (`t.` and `c.`). Previously both were
 * fetched inline and unbounded — Sofia's page pulled 489 procurements in a
 * 204 KB response and would have pulled thousands once the archive is in.
 */

import { useQuery } from '@tanstack/react-query';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { CompanyList, TenderList } from '../components/lists';
import { DataTable } from '../components/DataTable';
import {
  Amount, Badge, Breadcrumb, DetailHead, Disclosure, ErrorState, Fact, GraphButton, Link, Loading,
  SectionHead,
} from '../components/ui';
import { date, mandateTerm, nominationFor, num, truncate, validity } from '../lib/format';
import { useHasScopedParams } from '../lib/router';

/* Deliberately a second request. It is the one query on this page that
   can put a named person beside a public contract, so it is kept apart
   from the dossier it annotates — the page renders whether or not it
   answers, and nothing else waits on it. */
const INTEREST = graphql(`
  query CouncilInterest($key: ID!) {
    councilInterestLeads(municipality: $key) {
      councillorHash councillorName electedYear sitting
      uic companyName legalForm roles namesakes awards value
    }
  }
`);

const MUNICIPALITY = graphql(`
  query MunicipalityDossier($key: ID!) {
    municipalities(where: { key: $key }) {
      key
      name
      province
      ekatte
      population_permanent
      population_current
      population_as_of
      tendersAggregate { count }
      companiesAggregate { count }
      mayorsConnection {
        edges {
          properties { elected_year term_end elected_round }
          node {
            person_hash
            name
            nominatedByConnection {
              edges { properties { election_year } node { key name } }
            }
          }
        }
      }
      # Кметове на кметства — the mayors of the villages and towns inside
      # the municipality. Thousands of them authorise spending in their own
      # settlement and none of them appeared anywhere in the interface,
      # so a name found through a family tie led to a page that did not
      # mention the office.
      villageMayors: officeHoldersConnection(
        where: { edge: { NOT: { role: "общински съветник" } } }
        sort: [{ edge: { elected_year: DESC } }]
      ) {
        edges {
          properties { role area elected_year term_end }
          node {
            person_hash
            name
            company_links
            nominatedByConnection {
              edges { properties { election_year } node { key name } }
            }
          }
        }
      }
      officeHoldersConnection(
        where: { edge: { role: "общински съветник" } }
        sort: [{ edge: { elected_year: DESC } }]
      ) {
        edges {
          properties {
            role area_code elected_year term_end list_letter preferences
            replaces replaces_office
          }
          node {
            person_hash
            name
            company_links
            nominatedByConnection {
              edges { properties { election_year } node { key name } }
            }
          }
        }
      }
      administration {
        uic
        name
        legal_form
        status
        managersConnection {
          edges { properties { role valid_from valid_to } node { person_hash name } }
        }
        units { uic name }
      }
    }
    tendersAggregate(where: { commissionedBy_SOME: { key: $key } }) {
      count
      value_bgn { sum }
    }
  }
`);

/* The prop is not called `key`: React reserves that for reconciliation and
   would consume it instead of passing it down. */
export function MunicipalityPage({ municipalityKey }: { municipalityKey: string }) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['municipality', municipalityKey],
    queryFn: () => request(MUNICIPALITY, { key: municipalityKey }),
  });
  const interest = useQuery({
    queryKey: ['council-interest', municipalityKey],
    queryFn: () => request(INTEREST, { key: municipalityKey }),
  });
  /* A shared link that carries a filter opens the section it filters,
     whatever that section holds. Before the early returns: hooks. */
  const deepLinked = {
    tenders: useHasScopedParams('t.'),
    companies: useHasScopedParams('c.'),
  };

  if (isPending) return <Loading />;
  if (isError) return <ErrorState message={(error).message} />;

  const municipality = data.municipalities[0];
  if (!municipality) return <ErrorState message="Общината не е намерена в графа." />;

  // Newest mandate first. A municipality outlives its mayors, so this is a
  // list of terms; the sitting one is the term no later election closed.
  const mandates = [...municipality.mayorsConnection.edges]
    .sort((a, b) => (b.properties.elected_year ?? 0) - (a.properties.elected_year ?? 0));
  const sitting = mandates.find((edge) => edge.properties.term_end == null) ?? mandates[0] ?? null;
  const party = nominationFor(sitting);

  /* The council, newest election first, and within it by party strength —
     which is the order the seats were actually filled in. */
  const councilByYear = new Map<number, typeof municipality.officeHoldersConnection.edges>();
  for (const edge of municipality.officeHoldersConnection.edges) {
    const year = edge.properties.elected_year ?? 0;
    councilByYear.set(year, [...(councilByYear.get(year) ?? []), edge]);
  }
  const councilYears = [...councilByYear.keys()].sort((a, b) => b - a);
  const council = councilYears.length ? councilByYear.get(councilYears[0]!)! : [];
  /* Seats per party, which is the shape of a council: who has to agree with
     whom before anything passes. */
  const blocs = [...council.reduce((acc, edge) => {
    const nomination = nominationFor(edge);
    const name = nomination?.name ?? 'независим';
    const entry = acc.get(name) ?? { name, key: nomination?.key ?? null, seats: 0 };
    entry.seats += 1;
    acc.set(name, entry);
    return acc;
  }, new Map<string, { name: string; key: string | null; seats: number }>()).values()]
    .sort((a, b) => b.seats - a.seats);

  /* Whether the mayor's party can carry a vote in the council that adopts
     his budget. The mayor is the executive; the council authorises. They
     are the same party in 104 of 265 municipalities — everywhere else the
     spending is agreed with somebody, and in 52 the council is led by
     another party altogether. */
  const mayorParty = party?.key ?? null;
  const mayorBloc = blocs.find((bloc) => bloc.key && bloc.key === mayorParty) ?? null;
  const councilSize = council.length;
  const largest = blocs[0] ?? null;
  const control = !councilSize || !mayorParty ? null : {
    seats: mayorBloc?.seats ?? 0,
    majority: (mayorBloc?.seats ?? 0) * 2 > councilSize,
    leads: Boolean(mayorBloc && largest && mayorBloc.seats === largest.seats),
    largest,
  };

  /* Кметове на кметства, sitting term first. A municipality holds dozens
     of them and each authorises spending in their own settlement, so the
     list is grouped by term rather than merged — a village mayor who lost
     in 2023 is not a village mayor. */
  const villageMayors = [...municipality.villageMayors.edges]
    .sort((a, b) => (b.properties.elected_year ?? 0) - (a.properties.elected_year ?? 0)
      || (a.properties.area ?? '').localeCompare(b.properties.area ?? '', 'bg'));
  const sittingVillageMayors = villageMayors.filter((edge) => edge.properties.term_end == null);

  const spend = data.tendersAggregate.value_bgn?.sum ?? 0;
  const perCapita = municipality.population_permanent
    ? spend / municipality.population_permanent
    : null;

  return (
    <>
      <Breadcrumb>{[
        <Link key="up" href="#/municipalities">Общини</Link>,
        municipality.name ?? municipalityKey,
      ]}</Breadcrumb>

      <DetailHead
        eyebrow="Община"
        title={municipality.name ?? municipalityKey}
        meta={[
          municipality.province ? ['Област', municipality.province] as const : null,
          municipality.ekatte ? ['ЕКАТТЕ', municipality.ekatte] as const : null,
          sitting ? ['Кмет', (
            <Link href={`#/people/${sitting.node.person_hash}`}>{sitting.node.name}</Link>
          )] as const : null,
          party ? ['Партия', (
            <Link href={`#/parties/${encodeURIComponent(party.key)}`}>
              {truncate(party.name, 34)}
            </Link>
          )] as const : null,
        ]}
        actions={(
          <>
            <GraphButton href={`#/graph?municipality=${encodeURIComponent(municipalityKey)}`} />
            {' '}
            <GraphButton href={`#/activity?municipality=${encodeURIComponent(municipalityKey)}`}>
              Карта на дейността
            </GraphButton>
          </>
        )}
      />

      <div className="facts">
        <Fact value={num(municipality.population_permanent)} label="Постоянен адрес" />
        <Fact value={num(municipality.population_current)} label="Настоящ адрес" />
        <Fact value={num(data.tendersAggregate.count)} label="Възложени поръчки" />
        <Fact value={<Amount value={spend} />} label="Обща стойност" />
        <Fact value={<Amount value={perCapita} />} label="На жител" />
      </div>

      {/* Who governed, and when. ЦИК publishes structured results back to
          2015, which is what makes "who was mayor when this was awarded"
          answerable — no single "current mayor" field can express it. */}
      {mandates.length > 1 && (
        <section className="block">
          <SectionHead title="Кметски мандати" aside="по данни на ЦИК" />
          <DataTable
            rows={mandates}
            rowKey={(edge) => String(edge.properties.elected_year)}
            columns={[
              {
                key: 'term',
                label: 'Мандат',
                render: (edge) => <span className="mono">{mandateTerm(edge.properties)}</span>,
              },
              {
                key: 'mayor',
                label: 'Кмет',
                render: (edge) => (
                  <Link href={`#/people/${edge.node.person_hash}`}>{edge.node.name}</Link>
                ),
              },
              {
                key: 'party',
                label: 'Издигнат от',
                render: (edge) => {
                  const nomination = nominationFor(edge);
                  return nomination
                    ? (
                      <Link href={`#/parties/${encodeURIComponent(nomination.key)}`}>
                        {truncate(nomination.name, 44)}
                      </Link>
                    )
                    : '—';
                },
              },
              {
                key: 'round',
                label: 'Избран',
                render: (edge) => (edge.properties.elected_round === 2 ? 'на балотаж' : 'на първи тур'),
              },
              {
                key: 'status',
                label: '',
                render: (edge) => (edge.properties.term_end == null
                  ? <Badge kind="municipality">настоящ</Badge>
                  : null),
              },
            ]}
          />
        </section>
      )}

      {/* Общински съвет. The councillors vote the budget the mayor then
          spends, which is why they belong here at least as much as the
          mayor does — and unlike the mayor they are computed rather than
          published, so every seat shown reconciles with ЦИК's own
          allocation or is not shown at all. See etl/sources/cik_council.py. */}
      {council.length > 0 && (
        <Disclosure
          title="Общински съвет"
          aside={`${num(council.length)} съветници · избран ${councilYears[0]}`}
        >
          {/* The composition before the names: a council is read by who has
              to agree with whom, not by who is on it. */}
          <div className="bloc-bar" role="img"
            aria-label={blocs.map((bloc) => `${bloc.name}: ${bloc.seats}`).join(', ')}>
            {blocs.map((bloc, index) => (
              <span
                key={bloc.name}
                className={`bloc bloc-${index % 6}`}
                style={{ flexGrow: bloc.seats }}
                title={`${bloc.name} — ${bloc.seats} места`}
              >
                {bloc.seats}
              </span>
            ))}
          </div>
          {control && (
            <p className="council-control">
              {control.majority
                ? `Партията на кмета има ${control.seats} от ${councilSize} места — мнозинство.`
                : control.leads
                  ? `Партията на кмета е най-голямата с ${control.seats} от ${councilSize} места, `
                    + 'но без мнозинство: бюджетът минава със съгласието на друга група.'
                  : `Партията на кмета има ${control.seats} от ${councilSize} места. `
                    + `Съветът се води от ${control.largest?.name ?? 'друга група'}`
                    + `${control.largest ? ` с ${control.largest.seats}` : ''}.`}
              {' '}
              <span className="muted">
                Кметът е изпълнителната власт; бюджетът и разходите се приемат от съвета.
              </span>
            </p>
          )}

          <ul className="bloc-key">
            {blocs.map((bloc, index) => (
              <li key={bloc.name}>
                <span className={`bloc-dot bloc-${index % 6}`} aria-hidden="true" />
                {bloc.key
                  ? (
                    <Link href={`#/parties/${encodeURIComponent(bloc.key)}`}>
                      {truncate(bloc.name, 40)}
                    </Link>
                  )
                  : truncate(bloc.name, 40)}
                <span className="muted">{` · ${bloc.seats}`}</span>
              </li>
            ))}
          </ul>

          <DataTable
            rows={council}
            rowKey={(edge) => `${edge.node.person_hash}:${edge.properties.area_code}`}
            columns={[
              {
                key: 'name',
                label: 'Съветник',
                render: (edge) => (
                  <div>
                    <Link href={`#/people/${edge.node.person_hash}`}>{edge.node.name}</Link>
                    {edge.properties.replaces && (
                      <div className="uic">
                        {`на мястото на ${edge.properties.replaces}`}
                        {edge.properties.replaces_office
                          ? `, избран за ${edge.properties.replaces_office}` : ''}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'party',
                label: 'Издигнат от',
                render: (edge) => {
                  const nomination = nominationFor(edge);
                  return nomination
                    ? (
                      <Link href={`#/parties/${encodeURIComponent(nomination.key)}`}>
                        {truncate(nomination.name, 36)}
                      </Link>
                    )
                    : '—';
                },
              },
              {
                /* Preferences are why a list's order is not its seats'
                   order: at least 7% of the list's vote moves a candidate
                   into list А, and the rest follow as the party filed them. */
                key: 'preferences',
                label: 'Преференции',
                num: true,
                render: (edge) => (edge.properties.preferences
                  ? (
                    <span title={edge.properties.list_letter === 'А'
                      ? 'над 7% от гласовете за листата — подреден по преференции'
                      : 'под прага — подреден по реда на листата'}>
                      {num(edge.properties.preferences)}
                      <span className="muted">{` ${edge.properties.list_letter ?? ''}`}</span>
                    </span>
                  )
                  : '—'),
              },
              {
                /* Where on which list they stood, which is what ЦИК's own
                   allocation page identifies a councillor by — and what
                   tells two people of the same name on one council apart.
                   Deliberately not "companies owned": a councillor's node
                   comes from ЦИК, which publishes no ЕГН, and a company
                   officer's from the register, which publishes it already
                   hashed. The same person is two nodes, so that column
                   would read 0 for everyone and look like a finding. */
                key: 'seat',
                label: 'Листа',
                num: true,
                render: (edge) => (
                  <span className="mono muted">
                    {(edge.properties.area_code ?? '').split('-').slice(1).join(' · ') || '—'}
                  </span>
                ),
              },
            ]}
          />
        </Disclosure>
      )}

      {/* Кметове на кметства. They authorise spending in their own
          settlement, which is why they are in the graph — and until now
          they were in it and nowhere else, so a name reached through a
          family tie led to a page that never mentioned the office. */}
      {villageMayors.length > 0 && (
        <Disclosure
          title="Кметове на кметства"
          aside={sittingVillageMayors.length
            ? `${num(sittingVillageMayors.length)} в настоящия мандат`
            : `${num(villageMayors.length)} мандата`}
          defaultOpen={false}
        >
          <p className="dossier-prose">
            Кметът на кметство е избран пряко и разпорежда бюджет в своето
            населено място. Показани са всички заредени мандати; „настоящ“
            означава мандат, който по-късен избор не е закрил.
          </p>
          <DataTable
            rows={villageMayors}
            rowKey={(edge) => `${edge.node.person_hash}:${edge.properties.area}`
              + `:${edge.properties.elected_year}`}
            columns={[
              {
                key: 'area',
                label: 'Кметство',
                render: (edge) => (
                  <div>
                    {edge.properties.area ?? '—'}
                    {edge.properties.term_end == null && (
                      <> <Badge kind="municipality">настоящ</Badge></>
                    )}
                  </div>
                ),
              },
              {
                key: 'mayor',
                label: 'Кмет',
                render: (edge) => (
                  <Link href={`#/people/${edge.node.person_hash}`}>{edge.node.name}</Link>
                ),
              },
              {
                key: 'party',
                label: 'Издигнат от',
                render: (edge) => {
                  const nomination = nominationFor(edge);
                  return nomination
                    ? (
                      <Link href={`#/parties/${encodeURIComponent(nomination.key)}`}>
                        {truncate(nomination.name, 32)}
                      </Link>
                    )
                    : '—';
                },
              },
              {
                key: 'term',
                label: 'Мандат',
                num: true,
                render: (edge) => (
                  <span className="mono">
                    {edge.properties.term_end
                      ? `${edge.properties.elected_year} – ${edge.properties.term_end}`
                      : `${edge.properties.elected_year} –`}
                  </span>
                ),
              },
            ]}
          />
        </Disclosure>
      )}

      {/* Councillors and municipal money. Every word here is chosen to
          claim only what is true: two records share a name and a place.
          The identity is not established and cannot be — ЦИК publishes no
          ЕГН and the register publishes it hashed — so the section names
          what to check rather than what was found. */}
      {(interest.data?.councilInterestLeads.length ?? 0) > 0 && (
        <Disclosure
          title="Съветници и общински пари"
          aside="съвпадения за проверка"
          defaultOpen={false}
        >
          <p className="dossier-prose">
            Съветник може да има бизнес — законът го позволява. Тук са случаите,
            в които <strong>фирма със седалище в общината е спечелила поръчка от
            същата община</strong>, а неин собственик или управител носи същото
            име като действащ или бивш общински съветник.{' '}
            <strong>Това не е установена самоличност.</strong> Съветниците идват
            от протоколите на ЦИК, които не публикуват ЕГН, а собствениците — от
            Търговския регистър, който го публикува вече хеширан; двата записа
            за едно и също лице не могат да бъдат свързани. Съвпадат име и
            населено място, нищо повече. Колоната „лица с това име“ показва колко
            души с това име държат фирми в регистъра: 1 прави съвпадението
            голямо, повече от 1 — слабо. Дали има конфликт на интереси зависи от
            декларациите по ЗПК и от протоколите на съвета, които не се публикуват
            машинночетимо.
          </p>
          <DataTable
            rows={interest.data?.councilInterestLeads ?? []}
            rowKey={(row) => `${row.councillorHash}:${row.uic}:${row.electedYear}`}
            columns={[
              {
                key: 'councillor',
                label: 'Съветник',
                render: (row) => (
                  <div>
                    <Link href={`#/people/${row.councillorHash}`}>{row.councillorName}</Link>
                    <div className="uic">
                      {row.sitting ? 'настоящ мандат' : `мандат ${row.electedYear}`}
                    </div>
                  </div>
                ),
              },
              {
                key: 'company',
                label: 'Фирма със същото име',
                render: (row) => (
                  <div>
                    <Link href={`#/companies/${row.uic}`}>
                      {truncate(row.companyName ?? row.uic, 34)}
                    </Link>
                    <div className="uic mono">
                      {row.uic}
                      {row.legalForm ? ` · ${row.legalForm}` : ''}
                      {` · ${row.roles.map((role) => (role === 'OWNER' ? 'собственик' : 'управител')).join(', ')}`}
                    </div>
                  </div>
                ),
              },
              {
                key: 'namesakes',
                label: 'Лица с това име',
                num: true,
                render: (row) => (row.namesakes === 1
                  ? <span className="amount" title="Само едно лице с това име държи фирми в регистъра">1</span>
                  : (
                    <span className="muted"
                      title="Няколко лица с това име държат фирми — съвпадението е слабо">
                      {num(row.namesakes)}
                    </span>
                  )),
              },
              {
                key: 'awards',
                label: 'Поръчки от общината',
                num: true,
                render: (row) => (
                  <div>
                    <span className="amount"><Amount value={row.value} /></span>
                    <div className="uic mono">{`${num(row.awards)} поръчки`}</div>
                  </div>
                ),
              },
            ]}
          />
        </Disclosure>
      )}

      {/* The administration is a legal entity of its own, with the mayor as
          its registered representative. */}
      {municipality.administration.length > 0 && (
        <section className="block">
          <SectionHead title="Общинска администрация" aside="по данни на БУЛСТАТ" />
          {municipality.administration.map((body) => (
            <div key={body.uic} className="admin-body">
              <Link href={`#/companies/${body.uic}`}>{body.name ?? body.uic}</Link>
              <span className="uic mono">
                {[body.uic, body.legal_form, body.status].filter(Boolean).join(' · ')}
              </span>
              {body.managersConnection.edges.length > 0 && (
                <DataTable
                  rows={body.managersConnection.edges}
                  rowKey={(edge) => edge.node.person_hash}
                  columns={[
                    {
                      key: 'who',
                      label: 'Представител',
                      render: (edge) => (
                        <Link href={`#/people/${edge.node.person_hash}`}>{edge.node.name}</Link>
                      ),
                    },
                    { key: 'role', label: 'Длъжност', render: (edge) => edge.properties.role ?? '—' },
                    { key: 'since', label: 'Период', render: (edge) => validity(edge.properties) },
                  ]}
                />
              )}
            </div>
          ))}
        </section>
      )}

      {/* Same rule as the company dossier: a small municipality that has
          awarded nothing should say so in a line, not in a screenful of
          empty filters. */}
      <Disclosure
        title="Възложени поръчки"
        aside={`${num(data.tendersAggregate.count)} общо`}
        defaultOpen={data.tendersAggregate.count > 0 || deepLinked.tenders}
      >
        <TenderList prefix="t." scope={{ buyerKeys: [municipalityKey] }} />
      </Disclosure>

      <Disclosure
        title="Фирми със седалище в общината"
        aside={`${num(municipality.companiesAggregate?.count)} общо`}
        defaultOpen={(municipality.companiesAggregate?.count ?? 0) > 0 || deepLinked.companies}
      >
        <CompanyList prefix="c." scope={{ seatKeys: [municipalityKey] }} />
      </Disclosure>

      {municipality.population_as_of && (
        <p className="colophon-note">
          Населението е по данни на ГРАО към {date(municipality.population_as_of)}.
        </p>
      )}
    </>
  );
}
